import { useAlarm } from "@src/services/api/hooks";
import { ScreenContainer } from "@src/components";
import { DataStateView } from "@src/components/screen-state";
import { useRouter } from "expo-router";
import AiFloatingActionButton from "@src/components/AiFloatingActionButton";
import { FEATURE_FLAGS } from "@src/services/feature-flags/feature-flags";
import { FeatureGate } from "@src/services/feature-flags/FeatureGate";
import { useTenantId } from "@src/features/tenants";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import { AlarmSettingsContent } from "@src/features/alarms/components/AlarmSettingsContent";
import { buildAlarmRoutePaths } from "../utils/alarm-routes";

interface AlarmSettingsProps {
  /**
   * Site ID for the alarm settings
   */
  siteId?: string;
  /**
   * Camera key for the alarm settings
   */
  cameraKey?: string;
  /**
   * Whether to show camera selection UI (affects spacing and AI button variant)
   */
  showCameraSelection?: boolean;
}

export function AlarmSettings({ siteId, cameraKey, showCameraSelection = false }: AlarmSettingsProps) {
  const router = useRouter();
  const tenantId = useTenantId();
  const effectiveSiteId = siteId;
  const safeSiteId = effectiveSiteId ?? "";
  const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  const selectedCamera = cameraKey || "";
  const alarmRoutes = buildAlarmRoutePaths({
    tenantId,
    siteId: safeSiteId,
    cameraKey: selectedCamera,
  });

  const { data: alarm, isLoading, isFetching, error, refetch } = useAlarm(safeSiteId, selectedCamera);

  const { bottom: safeAreaBottom } = useSafeAreaInsets();

  // Show error if siteId is missing (query will be disabled)
  const missingSiteIdError =
    selectedCamera && !effectiveSiteId ? new Error("Site ID is required to load alarm settings") : null;

  // Get the camera key string for navigation
  const cameraKeyStr = selectedCamera;

  return (
    <ScreenContainer edges={[]}>
      <DataStateView
        data={alarm}
        isLoading={selectedCamera && effectiveSiteId ? isLoading : false}
        isFetching={isFetching}
        error={missingSiteIdError || error}
        refetch={refetch}
        loadingMessage="Loading alarm settings..."
        errorTitle="Error Loading Alarm"
        errorMessage={
          missingSiteIdError ?
            "Unable to determine site ID for this camera. Please try selecting the camera again."
          : `Failed to load alarm data for camera: ${selectedCamera}`
        }
        emptyState={{
          icon: "bellOffOutline",
          title: "No Alarms",
          message: "No alarms configured for this camera.",
        }}
      >
        {(alarmData) => (
          <AlarmSettingsContent
            alarmData={alarmData}
            siteId={effectiveSiteId ?? ""}
            cameraKey={cameraKeyStr}
            showCameraSelection={showCameraSelection}
            safeAreaBottom={safeAreaBottom}
            isLoading={isLoading}
            isFetching={isFetching}
            error={error}
          />
        )}
      </DataStateView>

      <FeatureGate flag={FEATURE_FLAGS.AI_SCHEDULER}>
        <AiFloatingActionButton
          variant={showCameraSelection ? "tab" : "default"}
          onPress={() =>
            router.push(
              `/agent?cameraKey=${encodeURIComponent(cameraKeyStr)}&title=${encodeURIComponent("AI Schedule Assistant")}&tenantId=${encodeURIComponent(tenantId)}&siteId=${encodeURIComponent(safeSiteId)}`,
            )
          }
        />
      </FeatureGate>
    </ScreenContainer>
  );
}
